import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import KioskScreen from './screens/KioskScreen';
import EmployeeDashboardScreen from './screens/EmployeeDashboardScreen';
import AdminScreen from './screens/AdminScreen';
import EmployeeReportScreen from './screens/EmployeeReportScreen';
import { useTimeClock } from './hooks/useTimeClock';
import { EmployeeRole } from '../shared/types.js';
import Button from './components/Button';
import SetupWizardScreen from './screens/SetupWizardScreen';

const AdminLogin: React.FC = () => {
    const [pin, setPin] = React.useState('');
    const [error, setError] = React.useState('');
    const navigate = useNavigate();
    const { employees, isLoading } = useTimeClock();

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white">
                <h1 className="text-4xl font-bold animate-pulse">Loading Application...</h1>
            </div>
        );
    }

    const handleLogin = React.useCallback(() => {
        const adminEmployees = employees.filter(e => e.role === EmployeeRole.ADMIN);
        
        if (adminEmployees.length === 0) {
            setError('No admin accounts found. Please complete the setup wizard.');
            return;
        }

        const isAdmin = adminEmployees.some(admin => admin.pin === pin);

        if (isAdmin) {
            localStorage.setItem('isAdminAuthenticated', 'true');
            navigate('/admin');
        } else {
            setError('Invalid Admin PIN');
            setPin('');
        }
    }, [employees, navigate, pin, setError]);

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                handleLogin();
            } else if (event.key >= '0' && event.key <= '9') {
                event.preventDefault();
                setPin(p => (p.length < 8 ? p + event.key : p));
            } else if (event.key === 'Backspace') {
                event.preventDefault();
                setPin(p => p.slice(0, -1));
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleLogin]);

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Admin Login</h1>
            <input 
                type="password"
                readOnly
                value={pin.replace(/./g, '*')}
                className="w-[224px] text-center text-3xl p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white mb-4"
            />
            {error && <p className="text-red-500 mb-4">{error}</p>}
            <div className="grid grid-cols-3 gap-4">
                {[...Array(9).keys()].map(i => i + 1).map(num => (
                    <button key={num} onClick={() => setPin(pin + num)} className="p-4 text-2xl font-bold bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700 active:bg-gray-400 dark:active:bg-gray-600 transition-colors">{num}</button>
                ))}
                <button onClick={() => setPin('')} className="p-4 text-2xl bg-red-800 font-bold text-white rounded-lg hover:bg-red-700 active:bg-red-600 transition-colors">C</button>
                <button onClick={() => setPin(pin + '0')} className="p-4 text-2xl bg-gray-200 font-bold dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700 active:bg-gray-400 dark:active:bg-gray-600 transition-colors">0</button>
                <button onClick={handleLogin} className="p-4 text-2xl bg-green-800 font-bold text-white rounded-lg hover:bg-green-700 active:bg-green-600 transition-colors">Go</button>
            </div>
            <div className="mt-6 w-[224px]">
                <Button variant="secondary" onClick={() => navigate('/')} className="w-full">Cancel</Button>
            </div>
        </div>
    );
};


const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isAuthenticated = localStorage.getItem('isAdminAuthenticated') === 'true';
    if (!isAuthenticated) {
        return <Navigate to="/admin-login" replace />;
    }
    return <>{children}</>;
};

const App: React.FC = () => {
    const { companySettings, isLoading } = useTimeClock();
    const [secretKeyStatus, setSecretKeyStatus] = React.useState<'loading' | 'present' | 'absent'>('loading');

    React.useEffect(() => {
        const checkKey = async () => {
            if ((window as any).electronAPI && typeof (window as any).electronAPI.getSecretKey === 'function') {
                try {
                    const key = await (window as any).electronAPI.getSecretKey();
                    setSecretKeyStatus(key ? 'present' : 'absent');
                } catch (e) {
                    setSecretKeyStatus('absent');
                }
            } else {
                setSecretKeyStatus('absent');
            }
        };
        checkKey();
    }, []);

    if (isLoading || secretKeyStatus === 'loading') {
         return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white">
                <h1 className="text-4xl font-bold animate-pulse">Loading Application...</h1>
            </div>
        );
    }
    
    if (secretKeyStatus === 'absent') {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white p-8 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500 mb-4">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <h1 className="text-3xl font-bold">Access Denied</h1>
                <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
                    This application can only be accessed through the official desktop or mobile app.
                </p>
            </div>
        );
    }

    if (!companySettings?.isSetupComplete) {
        return <SetupWizardScreen />;
    }

    return (
        <div 
            className="min-h-screen bg-gray-100 dark:bg-gray-dark text-gray-900 dark:text-white transition-colors duration-300"
            onContextMenu={(e) => e.preventDefault()}
        >
          <Routes>
            <Route path="/" element={<KioskScreen />} />
            <Route path="/dashboard" element={<EmployeeDashboardScreen />} />
            <Route path="/my-report" element={<EmployeeReportScreen />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin" element={
                <ProtectedRoute>
                    <AdminScreen />
                </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
    );
};

export default App;